/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/xpathUtils.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed                    |
 * |---|------------------------|-------------------|-----------------|---------------------------------|
 * | 1 | 2026-06-19             | Claude Sonnet 4.6 | XPath support   | Created — shared XPath utilities|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/components/InspectPanel.tsx (generateXPath display)
 * - apps/studio/src/device/recorder.ts           (auto-add indexed XPath candidate)
 * - apps/studio/src/device/resolver.ts           (evaluateXPath for replay)
 * - apps/studio/src/components/LocatorField.tsx  (quality heuristic display)
 *
 * EXPORTS
 * -------
 * XPathQuality            — "stable" | "indexed" | "fragile"
 * XPathResult             — { xpath, quality, note }
 * generateXPath()         — builds the best XPath from node + ancestors + flat tree
 * evaluateXPath()         — resolves a Mimiq-subset XPath against an A11y tree
 * getXPathQualityFromString() — heuristic quality from xpath string alone
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. evaluateXPath() supports only the XPath subset generated by generateXPath().
 *    It is NOT a full XPath 1.0 engine. Supported patterns:
 *      //ClassName[@attr="val"]
 *      //ClassName[@attr="val"][N]            (N = 1-based occurrence)
 *      //ClassName[@attr="val"]/ChildClass[@attr2="val2"]/...
 *      //ClassName[@attr="val"]/Child[@index="N"]/...
 *    String escaping: "value", 'value', concat("p1", '"', "p2")
 *
 * 2. @index in XPath = node.index (0-based child position among ALL siblings).
 *    This is the Android accessibility tree attribute, not XPath position.
 *    We use @index="N" rather than positional [N+1] because @index counts
 *    ALL sibling types; positional [N] counts only same-class siblings.
 *
 * 3. generateXPath() checks uniqueness against the full flat tree before marking
 *    any XPath "stable". Non-unique attributes with occurrence index are "indexed".
 *
 * =============================================================================
 */

import type { A11yNode } from "./treeParser.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export type XPathQuality = "stable" | "indexed" | "fragile";

export interface XPathResult {
  xpath:   string;
  quality: XPathQuality;
  note:    string;
}

// ── XPath value escaping ──────────────────────────────────────────────────────

/**
 * Wrap s for use inside an XPath attribute predicate.
 *   No double-quote  →  "value"
 *   No single-quote  →  'value'
 *   Both             →  concat("p1", '"', "p2")
 */
export function xpathVal(s: string): string {
  if (!s.includes('"')) return `"${s}"`;
  if (!s.includes("'")) return `'${s}'`;
  const parts = s.split('"');
  return `concat(${parts.map((p, i) => (i === 0 ? `"${p}"` : `'"', "${p}"`)).join(", ")})`;
}

// ── generateXPath ─────────────────────────────────────────────────────────────

/**
 * Generate the best XPath for node using a priority-ordered algorithm.
 *
 * Level 1 (stable)  — node has a unique attribute:
 *   //ClassName[@resource-id="value"]
 *   //ClassName[@content-desc="value"]
 *   //ClassName[@text="value"]
 *   //ClassName[@resource-id="..." and @text="..."]
 *
 * Level 2 (indexed) — node has a non-unique @resource-id:
 *   //ClassName[@resource-id="..."][N]   (N = 1-based visual occurrence)
 *
 * Level 3 (indexed/fragile) — no attrs on node; walk ancestors for unique anchor:
 *   //AnchorClass[@resource-id="..."]/ChildClass[@index="N"]/...
 *   Uses @index="N" (Android 0-based sibling attr) — correct for mixed-class siblings.
 *
 * Level 4 (fragile) — nothing useful; positional path from last 4 levels.
 */
export function generateXPath(
  node:      A11yNode,
  ancestors: A11yNode[],
  flat:      A11yNode[],
): XPathResult {
  const cls = node.className;

  function countAttr(field: "resourceId" | "contentDesc" | "text", val: string): number {
    return flat.filter((n) => n[field] === val).length;
  }

  // ── Level 1a: unique @resource-id ────────────────────────────────────────
  if (node.resourceId && countAttr("resourceId", node.resourceId) === 1) {
    return {
      xpath:   `//${cls}[@resource-id="${node.resourceId}"]`,
      quality: "stable",
      note:    "Unique resource-id — most stable locator.",
    };
  }

  // ── Level 1b: unique @content-desc ───────────────────────────────────────
  if (node.contentDesc && countAttr("contentDesc", node.contentDesc) === 1) {
    return {
      xpath:   `//${cls}[@content-desc=${xpathVal(node.contentDesc)}]`,
      quality: "stable",
      note:    "Unique content-desc (accessibility label).",
    };
  }

  // ── Level 1c: unique @text ────────────────────────────────────────────────
  if (node.text && countAttr("text", node.text) === 1) {
    return {
      xpath:   `//${cls}[@text=${xpathVal(node.text)}]`,
      quality: "stable",
      note:    "Unique text content.",
    };
  }

  // ── Level 1d: unique combination @resource-id AND @text ──────────────────
  if (node.resourceId && node.text) {
    const combo = flat.filter(
      (n) => n.resourceId === node.resourceId && n.text === node.text,
    );
    if (combo.length === 1) {
      return {
        xpath:   `//${cls}[@resource-id="${node.resourceId}" and @text=${xpathVal(node.text)}]`,
        quality: "stable",
        note:    "Unique combination of resource-id + text.",
      };
    }
  }

  // ── Level 2: non-unique @resource-id + visual occurrence index ───────────
  if (node.resourceId) {
    const all = flat.filter((n) => n.resourceId === node.resourceId);
    all.sort((a, b) => a.bounds.top - b.bounds.top || a.bounds.left - b.bounds.left);
    const pos = all.findIndex((n) => n === node);
    const occurrence = (pos >= 0 ? pos : 0) + 1;
    return {
      xpath:   `//${cls}[@resource-id="${node.resourceId}"][${occurrence}]`,
      quality: "indexed",
      note:    `${occurrence} of ${all.length} elements share this resource-id. [${occurrence}] picks the ${ordinal(occurrence)} (sorted top→bottom).`,
    };
  }

  // ── Level 3: ancestor anchor + @index path ────────────────────────────────
  for (let i = 0; i < ancestors.length; i++) {
    const anc = ancestors[i]!;
    let anchorXPath: string | null = null;

    if (anc.resourceId && countAttr("resourceId", anc.resourceId) === 1)
      anchorXPath = `//${anc.className}[@resource-id="${anc.resourceId}"]`;
    else if (anc.contentDesc && countAttr("contentDesc", anc.contentDesc) === 1)
      anchorXPath = `//${anc.className}[@content-desc=${xpathVal(anc.contentDesc)}]`;
    else if (anc.text && countAttr("text", anc.text) === 1)
      anchorXPath = `//${anc.className}[@text=${xpathVal(anc.text)}]`;

    if (!anchorXPath) continue;

    // Build path from anchor → intermediates → node using @index
    const pathParts: string[] = [];
    for (let j = i - 1; j >= 0; j--) {
      const mid = ancestors[j]!;
      pathParts.push(`${mid.className}[@index="${mid.index}"]`);
    }
    pathParts.push(`${cls}[@index="${node.index}"]`);

    const ancLabel = i === 0 ? "parent" : i === 1 ? "grandparent" : `${i + 1}-level ancestor`;
    const ancAttr  = anc.resourceId ? "resource-id" : anc.contentDesc ? "content-desc" : "text";

    return {
      xpath:   `${anchorXPath}/${pathParts.join("/")}`,
      quality: i <= 2 ? "indexed" : "fragile",
      note:    `Anchored by ${ancLabel}'s ${ancAttr}. @index="${node.index}" = Android's 0-based sibling position (unambiguous for mixed-class siblings — unlike XPath positional [N]).`,
    };
  }

  // ── Level 4: fragile positional path ─────────────────────────────────────
  const chain   = [...[...ancestors].reverse(), node];
  const trimmed = chain.length > 4 ? chain.slice(-4) : chain;
  return {
    xpath:   "//" + trimmed.map((n) => `${n.className}[@index="${n.index}"]`).join("/"),
    quality: "fragile",
    note:    "No unique identifier found. This XPath breaks when the UI hierarchy changes. Add an accessibility ID or text label to the element.",
  };
}

function ordinal(n: number): string {
  return n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
}

// ── getXPathQualityFromString ─────────────────────────────────────────────────

/**
 * Heuristic quality for a user-typed XPath string (no flat tree available).
 *   stable   — has attribute predicate with known-stable attr (@resource-id/@text/@content-desc)
 *              and no @index predicates
 *   indexed  — contains @index="N" (ancestor-anchor + position pattern)
 *   fragile  — positional only or very long with no attribute predicates
 */
export function getXPathQualityFromString(xpath: string): XPathQuality {
  if (!xpath || !xpath.startsWith("//")) return "fragile";
  const hasIndex   = /@index\s*=/.test(xpath);
  const hasStable  = /(@resource-id|@text|@content-desc)\s*=/.test(xpath);
  if (hasStable && !hasIndex) return "stable";
  if (hasIndex && hasStable)  return "indexed"; // anchor + index
  if (hasIndex)               return "indexed"; // index only
  // No attributes — just class path
  return "fragile";
}

// ── evaluateXPath ─────────────────────────────────────────────────────────────

/**
 * Evaluate a Mimiq-generated XPath against the A11y tree.
 *
 * Supported grammar (subset of XPath 1.0):
 *   START  := '//' SEGMENT ('/' SEGMENT)*
 *   SEGMENT := CLASSNAME ('[' PRED ']')*
 *   PRED    := '@attr' '=' STRVAL | NUMBER
 *   STRVAL  := '"' ... '"' | "'" ... "'"
 *   NUMBER  := [0-9]+   (1-based XPath occurrence among same-class matches)
 *
 * Attribute mapping (XPath → A11yNode field):
 *   @resource-id  →  node.resourceId
 *   @content-desc →  node.contentDesc
 *   @text         →  node.text
 *   @index        →  node.index (0-based; stored as string in XPath, compared numerically)
 *   @class        →  node.className (usually handled by CLASSNAME part)
 *
 * Returns the first matching A11yNode, or null if no match.
 */
export function evaluateXPath(xpath: string, rootNodes: A11yNode[]): A11yNode | null {
  if (!xpath || !xpath.startsWith("//")) return null;

  const body     = xpath.slice(2);    // strip leading //
  const segments = body.split("/");   // split on direct-child axis /
  if (segments.length === 0 || !segments[0]) return null;

  // Parse all segments
  const parsed = segments.map(parseSegment);

  // Collect all nodes matching the first segment (descendant-or-self search)
  let current: A11yNode[] = findDescendants(rootNodes, parsed[0]!);

  // For each subsequent segment, collect children matching that segment
  for (let i = 1; i < parsed.length; i++) {
    const seg = parsed[i]!;
    const next: A11yNode[] = [];
    for (const parent of current) {
      const children = parent.children.filter((c) => matchesSegment(c, seg));
      next.push(...children);
    }
    current = next;
  }

  // If last segment had an occurrence predicate, apply it (sorted visually)
  const lastSeg = parsed[parsed.length - 1]!;
  if (lastSeg.occurrence !== null) {
    current.sort((a, b) => a.bounds.top - b.bounds.top || a.bounds.left - b.bounds.left);
    return current[lastSeg.occurrence - 1] ?? null; // 1-based → 0-based
  }

  return current[0] ?? null;
}

// ── Internal XPath parsing ────────────────────────────────────────────────────

interface Segment {
  className:  string;
  predicates: Record<string, string>;
  occurrence: number | null;   // from [N] positional predicate (1-based), null if absent
}

function parseSegment(seg: string): Segment {
  // Extract className (everything before first '[')
  const bracketIdx = seg.indexOf("[");
  const className  = bracketIdx >= 0 ? seg.slice(0, bracketIdx) : seg;
  const predStr    = bracketIdx >= 0 ? seg.slice(bracketIdx) : "";

  const predicates: Record<string, string> = {};
  let occurrence: number | null = null;

  // Extract @attr=value predicates
  const attrRe = /\[@([a-zA-Z-]+)\s*=\s*([^\]]+?)\]/g;
  let m: RegExpExecArray | null;
  while ((m = attrRe.exec(predStr)) !== null) {
    const attr = m[1]!;
    let   val  = m[2]!.trim();
    // Strip enclosing quotes
    if ((val.startsWith('"') && val.endsWith('"')) ||
        (val.startsWith("'") && val.endsWith("'"))) {
      val = val.slice(1, -1);
    } else if (val.startsWith("concat(")) {
      val = evalConcat(val);
    }
    predicates[attr] = val;
  }

  // Extract positional predicate [N] (not an attribute predicate)
  const posRe = /\[(\d+)\]/g;
  let posM: RegExpExecArray | null;
  while ((posM = posRe.exec(predStr)) !== null) {
    occurrence = parseInt(posM[1]!, 10);
  }

  return { className, predicates, occurrence };
}

function matchesSegment(node: A11yNode, seg: Segment): boolean {
  // Class name must match (empty className = wildcard '*')
  if (seg.className !== "*" && node.className !== seg.className) return false;

  for (const [attr, val] of Object.entries(seg.predicates)) {
    switch (attr) {
      case "resource-id":  if (node.resourceId  !== val) return false; break;
      case "content-desc": if (node.contentDesc  !== val) return false; break;
      case "text":         if (node.text          !== val) return false; break;
      case "index":        if (String(node.index) !== val) return false; break;
      case "class":        if (node.className     !== val) return false; break;
      // boolean attrs
      case "clickable":    if (String(node.clickable)    !== val) return false; break;
      case "enabled":      if (String(node.enabled)      !== val) return false; break;
      case "focusable":    if (String(node.focusable)    !== val) return false; break;
      case "scrollable":   if (String(node.scrollable)   !== val) return false; break;
      case "checked":      if (String(node.checked)      !== val) return false; break;
      case "selected":     if (String(node.selected)     !== val) return false; break;
      // unknown attrs → skip (forward-compat)
    }
  }
  return true;
}

/** DFS over all descendants (including rootNodes themselves) matching segment. */
function findDescendants(roots: A11yNode[], seg: Segment): A11yNode[] {
  const results: A11yNode[] = [];
  function walk(nodes: A11yNode[]) {
    for (const n of nodes) {
      if (matchesSegment(n, seg)) {
        // If occurrence predicate, collect all matches first; handled by caller
        results.push(n);
      }
      walk(n.children);
    }
  }
  walk(roots);

  // Apply occurrence (positional [N]) among matches, sorted visually
  if (seg.occurrence !== null) {
    results.sort((a, b) => a.bounds.top - b.bounds.top || a.bounds.left - b.bounds.left);
    const found = results[seg.occurrence - 1];
    return found ? [found] : [];
  }

  return results;
}

/** Minimal concat("p1", '"', "p2") evaluator */
function evalConcat(expr: string): string {
  const inner = expr.slice("concat(".length, -1);
  const parts: string[] = [];
  for (const part of inner.split(",")) {
    const p = part.trim();
    if ((p.startsWith('"') && p.endsWith('"')) || (p.startsWith("'") && p.endsWith("'"))) {
      parts.push(p.slice(1, -1));
    } else {
      parts.push(p);
    }
  }
  return parts.join("");
}
